import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { partnerApiService, referencesApiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Category, CategoryGroup, City, GeocodedAddress, RegistrationRequest } from '../../types/api';
import { CODE_ALREADY_REGISTERED, apiErrorCode, apiErrorMessage, apiFieldErrors } from '../../services/apiErrors';
import { PARTNER_OFFER_UPDATED_AT, PARTNER_OFFER_URL, PARTNER_OFFER_VERSION } from '../../config/legal';
import { RegistrationDraft, readDraft, writeDraft } from '../../services/registrationDraft';
import { CategorySection, categorySections } from '../../utils/taxonomy';
import { hasCoordinates, parseCoordinate } from '../../utils/coordinates';
import { AddressByPoint, addressByPoint } from '../../utils/addressByPoint';
import { DEFAULT_COORDINATES } from '../../config/constants';
import { MapPoint } from '../map/PointPickerMap';
import { Notice, SelectField, SelectItem, Spinner, TextField } from './AuthParts';
import { VenuePointPicker, AddressLookup } from './VenuePointPicker';
import { useLanguage } from '../../contexts/LanguageContext';
import './auth.css';

interface RegistrationDetailsFormProps {
  /** Номер, к которому привязан черновик анкеты. */
  phone: string;
  onSuccess: () => void;
}

/** Длины из `RegistrationRequest` на бэке: показать ошибку до запроса, а не после. */
const LIMITS = {
  nameMin: 2,
  nameMax: 255,
  venueAddressMin: 5,
  venueAddressMax: 255,
};

/** С какой длины адрес ищется на сервере (`GET /partner/geocode`). */
const ADDRESS_SEARCH_MIN = 3;

type FieldKey = 'venue.name' | 'venue.city_id' | 'venue.address' | 'venue.category_id' | 'offer.accepted';

type Errors = Partial<Record<FieldKey, string>>;

interface FormState {
  venueName: string;
  cityId: string;
  venueAddress: string;
  categoryId: string;
  /** Точка, поставленная на карте; необязательна (ADR-025). */
  point: MapPoint | null;
  /**
   * Адрес, который форма подставила по точке. Пока поле равно ему, партнёр
   * адрес не вводил, и новая точка может его заменить.
   */
  autoFilledAddress: string | null;
  offerAccepted: boolean;
}

function pointFromDraft(draft: RegistrationDraft): MapPoint | null {
  const latitude = parseCoordinate(draft.latitude);
  const longitude = parseCoordinate(draft.longitude);
  return latitude !== undefined && longitude !== undefined && hasCoordinates(latitude, longitude)
    ? { latitude, longitude }
    : null;
}

function stateFromDraft(draft: RegistrationDraft): FormState {
  // Черновики, сохранённые, пока анкета спрашивала реквизиты, ещё несут
  // `legal_*` — сюда они не попадают, а первая же правка перезапишет черновик.
  return {
    venueName: draft.venue_name ?? '',
    venueAddress: draft.venue_address ?? '',
    cityId: draft.city_id != null ? String(draft.city_id) : '',
    categoryId: draft.category_id != null ? String(draft.category_id) : '',
    point: pointFromDraft(draft),
    autoFilledAddress: draft.venue_address_by_point ?? null,
    // Акцепт в черновик не кладём: галочку человек ставит осознанно и заново.
    offerAccepted: false,
  };
}

/** Зум карты: на точке видно здание, у найденного адреса — квартал, у города — весь город. */
const MAP_ZOOM = { point: 17, address: 16, city: 12 };

function cityCenter(cities: City[], cityId: string): MapPoint | null {
  const city = cities.find((c) => c.id === Number(cityId));
  const latitude = parseCoordinate(city?.latitude);
  const longitude = parseCoordinate(city?.longitude);
  return latitude !== undefined && longitude !== undefined ? { latitude, longitude } : null;
}

function required(value: string, message: string, min?: number, tooShort?: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return message;
  if (min && trimmed.length < min) return tooShort ?? message;
  return undefined;
}

function validate(state: FormState, authStrings?: any): Errors {
  const errors: Errors = {};

  errors['venue.name'] = required(
    state.venueName,
    authStrings?.validationVenueName ?? 'Укажите название заведения',
    LIMITS.nameMin,
    authStrings?.validationVenueNameShort ?? 'Название слишком короткое'
  );

  if (!state.cityId) errors['venue.city_id'] = authStrings?.validationCity ?? 'Выберите город';

  errors['venue.address'] = required(
    state.venueAddress,
    authStrings?.validationVenueAddress ?? 'Укажите фактический адрес заведения',
    LIMITS.venueAddressMin,
    authStrings?.validationVenueAddressShort ?? 'Адрес заведения слишком короткий'
  );

  if (!state.categoryId) errors['venue.category_id'] = authStrings?.validationCategory ?? 'Выберите основную категорию';
  if (!state.offerAccepted) errors['offer.accepted'] = authStrings?.validationOffer ?? 'Примите условия оферты';

  // `required` возвращает undefined для валидных полей — вычищаем, чтобы
  // наличие ключа не считалось ошибкой.
  (Object.keys(errors) as FieldKey[]).forEach((key) => {
    if (errors[key] === undefined) delete errors[key];
  });

  return errors;
}

/**
 * Разделы категорий (Поесть, Красота…) — только разбивка списка. Не
 * загрузились — категории идут плоским списком, как до разделов: анкета не
 * должна упираться в справочник, без которого выбрать категорию всё равно можно.
 */
async function loadCategoryGroups(): Promise<CategoryGroup[]> {
  try {
    return (await referencesApiService.getCategoryGroups()) ?? [];
  } catch (error) {
    console.error('Не загрузились разделы категорий, список будет плоским:', error);
    return [];
  }
}

/** Категории для `<select>`: раздел — `<optgroup>`, плоский список — просто пункты. */
function categoryItems(sections: CategorySection[]): SelectItem[] {
  return sections.flatMap<SelectItem>((section) => {
    const options = section.categories.map((category) => ({ value: category.id, label: category.title }));

    return section.title === null ? options : [{ label: section.title, options }];
  });
}

/**
 * Шаг 3 регистрации: данные заведения и акцепт оферты.
 *
 * Юридических реквизитов анкета не спрашивает — в запросе `venue` и `offer`.
 * Реквизиты партнёров, заведённых раньше, по-прежнему приходят в профиле
 * (`legal`) и показываются на главной кабинета.
 *
 * Сюда же человек попадает по коду `registration_incomplete` — если он закрыл
 * вкладку, не отправив анкету. Введённое к тому моменту лежит в черновике.
 */
export const RegistrationDetailsForm: React.FC<RegistrationDetailsFormProps> = ({ phone, onSuccess }) => {
  const { completeRegistration, logout } = useAuth();
  const { t } = useLanguage();

  const [state, setState] = useState<FormState>(() => stateFromDraft(readDraft(phone)));
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState('');
  const [cities, setCities] = useState<City[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryGroups, setCategoryGroups] = useState<CategoryGroup[]>([]);
  const [isLoadingReferences, setIsLoadingReferences] = useState(true);
  const [referencesError, setReferencesError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Карта открыта сразу, если точка уже стоит: человек вернулся к анкете.
  const [isMapOpen, setIsMapOpen] = useState(() => state.point !== null);
  const [mapFocusKey, setMapFocusKey] = useState(0);
  // Где нашёлся введённый адрес: карта открывается там, но точку не ставит.
  const [addressCenter, setAddressCenter] = useState<MapPoint | null>(null);
  const [lookup, setLookup] = useState<AddressLookup>({ status: 'idle' });
  // Номер последнего поиска адреса по точке. Растёт и тогда, когда ответ
  // больше не нужен: партнёр поставил новую точку, правит адрес руками или
  // уже отправил анкету.
  const lookupSeq = useRef(0);
  // То же для поиска введённого адреса при открытии карты: смена города
  // делает его ответ устаревшим.
  const openSeq = useRef(0);
  // Анкета на момент ответа геокодера: партнёр мог дописать адрес, пока ждал.
  const latestState = useRef(state);

  useEffect(() => {
    latestState.current = state;
  }, [state]);

  // Черновик пишем на каждое изменение: вкладку закрывают в любой момент,
  // а потерянная анкета — это потерянный партнёр.
  const patch = useCallback(
    (changes: Partial<FormState>) => {
      setState((current) => {
        const next = { ...current, ...changes };
        writeDraft(phone, {
          venue_name: next.venueName,
          venue_address: next.venueAddress,
          city_id: next.cityId ? Number(next.cityId) : null,
          category_id: next.categoryId ? Number(next.categoryId) : null,
          latitude: next.point?.latitude ?? null,
          longitude: next.point?.longitude ?? null,
          venue_address_by_point: next.autoFilledAddress,
        });
        return next;
      });
    },
    [phone]
  );

  const update = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => patch({ [key]: value } as Partial<FormState>),
    [patch]
  );

  useEffect(() => {
    let cancelled = false;

    // Плоский список категорий обязателен, как и города: без него выбирать
    // не из чего. Разделы — по возможности: их ошибка оставляет список
    // плоским, а не роняет анкету.
    Promise.all([referencesApiService.getCities(), referencesApiService.getCategories(), loadCategoryGroups()])
      .then(([loadedCities, loadedCategories, loadedGroups]) => {
        if (cancelled) return;
        setCities(loadedCities);
        setCategories(loadedCategories);
        setCategoryGroups(loadedGroups);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Не загрузились справочники регистрации:', error);
        setReferencesError(t.auth.referencesError);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingReferences(false);
      });

    return () => {
      cancelled = true;
    };
  }, [t.auth.referencesError]);

  const cityOptions = useMemo(
    () => cities.map((city) => ({ value: city.id, label: city.name })),
    [cities]
  );
  // Категории — по разделам, а не сплошным списком; порядок, дубли и
  // «Другое» решает `categorySections`.
  const categoryOptions = useMemo(
    () => categoryItems(categorySections(categoryGroups, categories, t.auth.venueCategoryOther)),
    [categoryGroups, categories, t.auth.venueCategoryOther]
  );

  /** Ответ, который ещё идёт, адрес уже не меняет. */
  const cancelAddressLookup = () => {
    lookupSeq.current += 1;
    setLookup({ status: 'idle' });
  };

  const handleAddressChange = (value: string) => {
    update('venueAddress', value);
    // Партнёр правит адрес сам: ответ по точке, если он ещё идёт, не должен
    // затереть набранное, а подсказка про адрес по точке — уже не про него.
    cancelAddressLookup();
  };

  const handleCityChange = (value: string) => {
    update('cityId', value);
    // Поиск адреса в прежнем городе больше не нужен: иначе его ответ увёз
    // бы карту обратно.
    openSeq.current += 1;
    // Точки ещё нет — карта переезжает в выбранный город.
    if (!state.point) {
      setAddressCenter(null);
      setMapFocusKey((key) => key + 1);
    }
  };

  /**
   * Карта открывается у введённого адреса: сервер ищет его в выбранном
   * городе. Точку по найденному не ставим — геокодер часто находит одну
   * улицу (ADR-014), а точку на здание партнёр ставит сам.
   */
  const handleOpenMap = async () => {
    setIsMapOpen(true);

    const address = state.venueAddress.trim();
    if (state.point || address.length < ADDRESS_SEARCH_MIN) return;

    const seq = ++openSeq.current;
    try {
      const [found] = await partnerApiService.geocodeAddress(address, Number(state.cityId) || null);
      if (seq !== openSeq.current) return;
      if (found) {
        setAddressCenter({ latitude: found.latitude, longitude: found.longitude });
        setMapFocusKey((key) => key + 1);
      }
    } catch (error) {
      // Карта остаётся на городе — здание найдут и так.
      console.error('Не нашёлся адрес для карты анкеты:', error);
    }
  };

  /**
   * Адрес по точке. Что делать с адресом в поле, решает `addressByPoint`:
   * найденный дом заменяет его, одна улица — только пустое поле или
   * подстановку по прошлой точке.
   */
  const lookUpAddress = async (point: MapPoint) => {
    const seq = ++lookupSeq.current;
    setLookup({ status: 'loading' });

    let found: GeocodedAddress | null = null;
    try {
      found = await partnerApiService.reverseGeocode(point.latitude, point.longitude);
    } catch (error) {
      console.error('Не определился адрес по точке:', error);
    }
    if (seq !== lookupSeq.current) return;

    const { venueAddress, autoFilledAddress } = latestState.current;
    const result: AddressByPoint = addressByPoint(venueAddress, found, {
      minLength: LIMITS.venueAddressMin,
      autoFilled: autoFilledAddress,
    });
    if (result.address !== venueAddress) {
      patch({ venueAddress: result.address, autoFilledAddress: result.address });
      setErrors((current) => {
        const next = { ...current };
        delete next['venue.address'];
        return next;
      });
    }
    setLookup({ status: 'done', result });
  };

  const handlePickPoint = (point: MapPoint) => {
    if (isSubmitting) return;
    update('point', point);
    void lookUpAddress(point);
  };

  // Карта смотрит на точку, без неё — на найденный адрес, потом на город.
  const mapCenter: MapPoint = state.point ?? addressCenter ?? cityCenter(cities, state.cityId) ?? DEFAULT_COORDINATES;
  const mapZoom = state.point ? MAP_ZOOM.point : addressCenter ? MAP_ZOOM.address : MAP_ZOOM.city;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    const found = validate(state, t.auth);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    // Анкета уходит с тем адресом, что в поле. Ответ по точке, пришедший
    // после, его уже не меняет — иначе после ошибки сервера человек видел бы
    // адрес, которого не отправлял.
    if (lookup.status === 'loading') cancelAddressLookup();

    const payload: RegistrationRequest = {
      venue: {
        name: state.venueName.trim(),
        city_id: Number(state.cityId),
        address: state.venueAddress.trim(),
        category_id: Number(state.categoryId),
        // Точка — только если партнёр её поставил: без неё анкета прежняя.
        ...(state.point ? { latitude: state.point.latitude, longitude: state.point.longitude } : {}),
      },
      // Редакцию оферты присылает клиент: сервер текст не хранит, он
      // записывает, что именно принял партнёр и когда.
      offer: { version: PARTNER_OFFER_VERSION, accepted: true },
    };

    setIsSubmitting(true);
    try {
      const response = await partnerApiService.submitRegistration(payload);
      // Ответ регистрации отдаёт партнёра коротко; полный профиль — с
      // категориями и статусом активации — кабинет подтянет сам при первом
      // же переходе.
      completeRegistration(response.partner);
      onSuccess();
    } catch (err) {
      // Двойной клик или ретрай после таймаута: заведение уже создано первой
      // отправкой, и это не ошибка — регистрация состоялась.
      if (apiErrorCode(err) === CODE_ALREADY_REGISTERED) {
        // Заведение известно только серверу — подставлять выдуманное нельзя,
        // кабинет загрузит профиль сам.
        completeRegistration(null);
        onSuccess();
        return;
      }

      const fieldErrors = apiFieldErrors(err);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors as Errors);
      }
      setFormError(apiErrorMessage(err, t.auth.submitFormError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="m3-auth-screen">
      <div className="m3-auth-card m3-auth-card--wide">
        <h1 className="m3-headline">{t.auth.orgDetailsTitle}</h1>
        <p className="m3-body" style={{ marginBottom: 24 }}>
          {t.auth.orgDetailsSubtitle}
        </p>

        {referencesError && <Notice tone="error">{referencesError}</Notice>}

        <form onSubmit={handleSubmit} className="qp-auth-form" noValidate>
          <section className="qp-auth-section">
            <h2 className="qp-auth-section-title">{t.auth.venueSectionTitle}</h2>
            <p className="qp-auth-section-hint">
              {t.auth.venueSectionHint}
            </p>

            <TextField
              id="venue-name"
              required
              label={t.auth.venueName}
              value={state.venueName}
              onChange={(value) => update('venueName', value)}
              placeholder={t.auth.venueNameDefaultPlaceholder}
              maxLength={LIMITS.nameMax}
              error={errors['venue.name']}
              disabled={isSubmitting}
            />

            <SelectField
              id="venue-city"
              required
              label={t.auth.city}
              value={state.cityId}
              onChange={handleCityChange}
              options={cityOptions}
              placeholder={isLoadingReferences ? t.auth.loadingReferences : t.auth.citySelect}
              error={errors['venue.city_id']}
              disabled={isSubmitting || isLoadingReferences}
            />

            <TextField
              id="venue-address"
              required
              label={t.auth.venueAddress}
              value={state.venueAddress}
              onChange={handleAddressChange}
              placeholder={t.auth.venueAddressPlaceholder}
              maxLength={LIMITS.venueAddressMax}
              error={errors['venue.address']}
              disabled={isSubmitting}
            />

            <VenuePointPicker
              isOpen={isMapOpen}
              onOpen={handleOpenMap}
              point={state.point}
              center={mapCenter}
              zoom={mapZoom}
              focusKey={mapFocusKey}
              onPick={handlePickPoint}
              lookup={lookup}
              disabled={isSubmitting}
            />

            <SelectField
              id="venue-category"
              required
              label={t.auth.venueCategory}
              value={state.categoryId}
              onChange={(value) => update('categoryId', value)}
              options={categoryOptions}
              placeholder={isLoadingReferences ? t.auth.loadingReferences : t.auth.venueCategorySelect}
              error={errors['venue.category_id']}
              disabled={isSubmitting || isLoadingReferences}
            />
          </section>

          <div className="qp-auth-offer">
            <label className="qp-auth-offer-row">
              <input
                type="checkbox"
                checked={state.offerAccepted}
                onChange={(e) => update('offerAccepted', e.target.checked)}
                disabled={isSubmitting}
                aria-invalid={errors['offer.accepted'] ? true : undefined}
              />
              <span>
                {t.auth.offerConsentPre}{' '}
                <Link to={PARTNER_OFFER_URL} target="_blank" rel="noopener noreferrer">
                  {t.auth.offerConsentLink}
                </Link>{' '}
                {t.auth.offerConsentEdition} {PARTNER_OFFER_UPDATED_AT}
              </span>
            </label>
            {errors['offer.accepted'] && (
              <span className="m3-field-support m3-field-support--error" role="alert">
                {errors['offer.accepted']}
              </span>
            )}
          </div>

          {formError && <Notice tone="error">{formError}</Notice>}

          <button
            type="submit"
            disabled={isSubmitting}
            className="m3-btn-filled m3-btn-filled--block"
          >
            {isSubmitting && <Spinner />}
            {isSubmitting ? t.auth.submitting : t.auth.submitRegistration}
          </button>
        </form>

        {/* Выход отсюда обязателен: пока анкета не отправлена, кабинет закрыт
            и любой другой адрес возвращает сюда же. Без этой строки человек,
            подтвердивший чужой номер или передумавший, заперт на экране. */}
        <p className="m3-body qp-auth-otherdoor">
          {t.auth.notYourNumber}{' '}
          <button type="button" className="qp-auth-linklike" onClick={logout}>
            {t.auth.logout}
          </button>
        </p>
      </div>
    </div>
  );
};
